import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../../../store/store';
import { useParams } from 'react-router-dom';
import { RootState } from '../../../reducers';
import {
    Box,
    Button,
    Checkbox,
    CircularProgress,
    Collapse,
    createTheme,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormControlLabel,
    FormGroup,
    FormLabel,
    Grid,
    IconButton,
    Paper,
    Radio,
    RadioGroup,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import { gotoNextMenu, gotoPreviousMenu } from '../../../store/assessment.slice';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import AlertDialog from '../../AlertDialog/AlertDialog';
import { copyObject, highlightStyle } from '../../../utils/common';
import { StatusEnum } from '../../../Enum/StatusEnum';
import BHDiagnosticConfirmationBody from '../../Shared/BHDiagnosticConfirmationBody';
import {
    fetchCurrentNeedOptions,
    fetchCurrentNeedsByMemberId,
    fetchCurrentNeedsInfo,
    saveCurrentNeedsAssessment,
    setCurrentNeedsInfo,
} from '../../../store/currentNeedsSlice.slice';
import { CurrentNeedsModel } from '../../../Models/CurrentNeedsModel/CurrentNeedsModel.model';
import { NO, YES } from '../../../utils/constants';
import { SingleNeed } from '../../../Models/CurrentNeedsModel/SingleNeed.model';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { toDate, formatDate } from '../../../utils/dateHelpers';
import { DiagnosticType } from '../../../Enum/DiagnosticType';
import { AssessmentQuestions } from '../../../utils/assessmentQuestions';
import { isAIIcon } from '../Assessment';
import InfoIcon from '@mui/icons-material/Info';
import HelpPage from '../../../assets/documents/Assessment_Current_Needs_Counseling_Subjective.pdf';

const theme = createTheme();
const classes = {
    form: {
        '& .MuiCheckbox-root': {
            marginLeft: '-10px',
        },
    },
    root: {
        '& .MuiGrid-root': {
            marginBottom: theme.spacing(2),
        },
        '& .MuiTextField-root': {
            margin: theme.spacing(1),
            width: '90%',
        },
    },
};

const yesNoList = [{ Answer: YES }, { Answer: NO }];

const CurrentNeeds: React.FC<{ memberId: string }> = ({ memberId }) => {
    const params = useParams();
    memberId = params.memberId;
    const dispatch = useAppDispatch();
    const [showSaveAlert, setShowSaveAlert] = useState<boolean>(false);
    const { engagementDraft, isSaving: isSavingEngagementDraft } = useSelector((state: RootState) => state.documentEngagement);
    const isBHDiagnostic = engagementDraft.DiagnosticStatus?.DiagnosticType === DiagnosticType.BHDiagnostic;
    const { currentNeedsInfo, currentNeedsOptions, isFetchingCurrentNeeds } = useSelector((state: RootState) => state.currentNeedsSlice);
    const isSaving = isSavingEngagementDraft || isFetchingCurrentNeeds;
    const [isChanged, setIsChanged] = useState<boolean>(false);
    const [editingComment, setEditingComment] = useState<{ optionLabel: string; index: number } | null>(null);
    const [editCommentText, setEditCommentText] = useState('');
    const [deletingComment, setDeletingComment] = useState<{ optionLabel: string; index: number } | null>(null);

    const currentNeedIsSelected = (values: CurrentNeedsModel) => {
        const isSelected = values.CurrentNeedsList.length > 0;
        return isSelected;
    };

    const currentNeedsInfoIsComplete = (values: CurrentNeedsModel) => {
        const isComplete =
            currentNeedIsSelected(values) &&
            values.CurrentNeedsList.every((need) => {
                if (need.IsMet === YES) {
                    return Boolean(need.HowNeedWasMet?.trim());
                }
                return true;
            });

        return isComplete;
    };

    const handleAddNeedComment = (singleNeed: SingleNeed) => {
        const copy = copyObject(currentNeedsInfo);
        const currentNeed = copy.CurrentNeedsList.find((o) => o.OptionLabel === singleNeed.OptionLabel);
        if (currentNeed) {
            if (currentNeed.NeedComments) {
                currentNeed.NeedComments = [{ Comment: currentNeed.Comments }, ...currentNeed.NeedComments];
            } else {
                currentNeed.NeedComments = [{ Comment: currentNeed.Comments }];
            }
            currentNeed.Comments = '';
            currentNeed.IsAI = false;
        }
        dispatch(setCurrentNeedsInfo(copy));
        setIsChanged(true);
    };

    const handleOpenEditNeedComment = (optionLabel: string, index: number, commentText: string) => {
        setEditingComment({ optionLabel, index });
        setEditCommentText(commentText || '');
    };

    const handleConfirmEditNeedComment = () => {
        if (!editingComment || !editCommentText.trim()) {
            return;
        }
        const copy = copyObject(currentNeedsInfo);
        const currentNeed = copy.CurrentNeedsList.find((o) => o.OptionLabel === editingComment.optionLabel);
        if (currentNeed?.NeedComments?.[editingComment.index]) {
            currentNeed.NeedComments[editingComment.index].Comment = editCommentText.trim();
            currentNeed.IsAI = false;
            dispatch(setCurrentNeedsInfo(copy));
            setIsChanged(true);
        }
        setEditingComment(null);
        setEditCommentText('');
    };

    const handleConfirmDeleteNeedComment = () => {
        if (!deletingComment) {
            return;
        }
        const copy = copyObject(currentNeedsInfo);
        const currentNeed = copy.CurrentNeedsList.find((o) => o.OptionLabel === deletingComment.optionLabel);
        if (currentNeed?.NeedComments) {
            currentNeed.NeedComments = currentNeed.NeedComments.filter((_, i) => i !== deletingComment.index);
            currentNeed.IsAI = false;
            dispatch(setCurrentNeedsInfo(copy));
            setIsChanged(true);
        }
        setDeletingComment(null);
    };

    const handleSubmit = () => {
        setShowSaveAlert(true);
    };

    const handleOnConfirmToNext = (currentNeedsInfo: CurrentNeedsModel, isComplete = false) => {
        let copy = copyObject(currentNeedsInfo);
        copy.MemberId = memberId;
        copy.IsAssessmentCompleted = isComplete;
        if (Boolean(isComplete)) {
            copy.AssessmentStatus = StatusEnum.Completed;
            dispatch(saveCurrentNeedsAssessment(copy, false, true));
        } else {
            copy.AssessmentStatus = StatusEnum.InProgress;
            dispatch(saveCurrentNeedsAssessment(copy, true, true));
        }
    };

    const handleOnConfirmSkip = () => {
        dispatch(setCurrentNeedsInfo(new CurrentNeedsModel()));
        dispatch(gotoNextMenu());
    };

    useEffect(() => {
        dispatch(fetchCurrentNeedOptions());
    }, [dispatch]);

    useEffect(() => {
        if (engagementDraft?.CurrentNeedsId) {
            dispatch(fetchCurrentNeedsInfo(engagementDraft.CurrentNeedsId));
        } else {
            dispatch(fetchCurrentNeedsByMemberId(memberId, true));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <Paper elevation={3} sx={{ padding: 2, marginBottom: 2 }}>
            <Grid container sx={classes.form} direction="column" spacing={2} className="content-panel">
                <Grid>
                    {isSaving ? (
                        <Box display="flex" justifyContent="center" className="loader-container">
                            <CircularProgress />
                        </Box>
                    ) : (
                        <>
                            <Grid container spacing={2} sx={classes.root}>
                                <Grid size={{ sm: 12, md: 12, lg: 12 }}>
                                    <Stack direction={'row'} alignItems={'center'} spacing={1}>
                                        <Typography variant="h6">Current Needs</Typography>
                                        <Tooltip title="Current Needs Help Document">
                                            <InfoIcon color="primary" fontSize="small" cursor="pointer" onClick={() => window.open(`${HelpPage}`, '_blank')} />
                                        </Tooltip>
                                    </Stack>
                                    {isBHDiagnostic && (
                                        <Typography variant="body2" component="label" ml={5} sx={highlightStyle}>
                                            * Required for BH Diagnostic
                                        </Typography>
                                    )}
                                    <hr />
                                </Grid>
                                <Grid size={12}>
                                    <FormLabel required={isBHDiagnostic} sx={isBHDiagnostic ? highlightStyle : undefined}>
                                        {AssessmentQuestions.CurrentNeeds_HasMemberIndicatedProvider}
                                    </FormLabel>
                                </Grid>
                                <FormControl sx={{ m: 3 }} component="fieldset" variant="standard">
                                    <FormGroup>
                                        <Grid container spacing={2}>
                                            <Grid size={6}>
                                                <Paper elevation={3} sx={{ p: 2 }}>
                                                    <FormControlLabel
                                                        sx={{ pl: 2 }}
                                                        control={
                                                            <Checkbox
                                                                checked={currentNeedsInfo.CurrentNeedsList.some((o) => o.OptionLabel === 'No needs')}
                                                                onChange={(e) => {
                                                                    const copy = copyObject(currentNeedsInfo);
                                                                    const currentNeed = copy.CurrentNeedsList.find((o) => o.OptionLabel === 'No needs');
                                                                    if (currentNeed) {
                                                                        copy.CurrentNeedsList = copy.CurrentNeedsList.filter(
                                                                            (o) => o.OptionLabel !== 'No needs'
                                                                        );
                                                                    } else {
                                                                        const newNeed = new SingleNeed();
                                                                        newNeed.OptionLabel = 'No needs';
                                                                        newNeed.Comments = '';
                                                                        copy.CurrentNeedsList.push(newNeed);
                                                                    }
                                                                    dispatch(setCurrentNeedsInfo(copy));
                                                                    setIsChanged(true);
                                                                }}
                                                            />
                                                        }
                                                        label={'No needs'}
                                                    />
                                                </Paper>
                                            </Grid>
                                            {currentNeedsOptions.map((option, index) => {
                                                return (
                                                    <Grid size={6} key={`${option.OptionLabel}-${index}`}>
                                                        <Paper elevation={3} sx={{ p: 2 }}>
                                                            <Stack>
                                                                <FormControlLabel
                                                                    sx={{ pl: 2 }}
                                                                    control={
                                                                        <Checkbox
                                                                            checked={currentNeedsInfo.CurrentNeedsList.some(
                                                                                (o) => o.OptionLabel === option.OptionLabel
                                                                            )}
                                                                            onChange={(e) => {
                                                                                const copy = copyObject(currentNeedsInfo);
                                                                                const currentNeed = copy.CurrentNeedsList.find(
                                                                                    (o) => o.OptionLabel === option.OptionLabel
                                                                                );
                                                                                if (currentNeed) {
                                                                                    copy.CurrentNeedsList = copy.CurrentNeedsList.filter(
                                                                                        (o) => o.OptionLabel !== option.OptionLabel
                                                                                    );
                                                                                } else {
                                                                                    const newNeed = new SingleNeed();
                                                                                    newNeed.OptionLabel = option.OptionLabel;
                                                                                    newNeed.Comments = '';
                                                                                    copy.CurrentNeedsList.push(newNeed);
                                                                                }
                                                                                dispatch(setCurrentNeedsInfo(copy));
                                                                                setIsChanged(true);
                                                                            }}
                                                                        />
                                                                    }
                                                                    label={
                                                                        <>
                                                                            {option.OptionLabel}
                                                                            {currentNeedsInfo.CurrentNeedsList.find((o) => o.OptionLabel === option.OptionLabel)
                                                                                ?.IsAI && isAIIcon()}
                                                                        </>
                                                                    }
                                                                />
                                                                <Collapse
                                                                    in={currentNeedsInfo.CurrentNeedsList.some((o) => o.OptionLabel === option.OptionLabel)}
                                                                >
                                                                    <Stack spacing={2}>
                                                                        <Stack direction={'row'} spacing={2} alignItems="center" pl={1}>
                                                                            <FormLabel required>Is this need met?</FormLabel>
                                                                            <RadioGroup
                                                                                row
                                                                                aria-labelledby="demo-row-radio-buttons-group-label"
                                                                                name="IsMet"
                                                                                value={
                                                                                    currentNeedsInfo.CurrentNeedsList.find(
                                                                                        (o) => o.OptionLabel === option.OptionLabel
                                                                                    )?.IsMet || null
                                                                                }
                                                                                onChange={(e) => {
                                                                                    const copy = copyObject(currentNeedsInfo);
                                                                                    const currentNeed = copy.CurrentNeedsList.find(
                                                                                        (o) => o.OptionLabel === option.OptionLabel
                                                                                    );
                                                                                    if (currentNeed) {
                                                                                        currentNeed.IsMet = e.target.value;
                                                                                        currentNeed.IsAI = false;
                                                                                        if (e.target.value === NO) {
                                                                                            currentNeed.HowNeedWasMet = null;
                                                                                        }
                                                                                    }
                                                                                    dispatch(setCurrentNeedsInfo(copy));
                                                                                    setIsChanged(true);
                                                                                }}
                                                                            >
                                                                                {yesNoList.map((item) => (
                                                                                    <FormControlLabel
                                                                                        key={item.Answer}
                                                                                        value={item.Answer}
                                                                                        control={<Radio />}
                                                                                        label={item.Answer}
                                                                                    />
                                                                                ))}
                                                                            </RadioGroup>
                                                                        </Stack>
                                                                        <Collapse
                                                                            sx={{ width: '100%', mt: 1 }}
                                                                            in={
                                                                                currentNeedsInfo.CurrentNeedsList.find(
                                                                                    (o) => o.OptionLabel === option.OptionLabel
                                                                                )?.IsMet === YES
                                                                            }
                                                                        >
                                                                            <TextField
                                                                                label="How was this need met?"
                                                                                variant="outlined"
                                                                                size="small"
                                                                                required={
                                                                                    currentNeedsInfo.CurrentNeedsList.find(
                                                                                        (o) => o.OptionLabel === option.OptionLabel
                                                                                    )?.IsMet === YES
                                                                                }
                                                                                fullWidth
                                                                                multiline
                                                                                rows={3}
                                                                                margin="normal"
                                                                                name="HowNeedWasMet"
                                                                                value={
                                                                                    currentNeedsInfo.CurrentNeedsList.find(
                                                                                        (o) => o.OptionLabel === option.OptionLabel
                                                                                    )?.HowNeedWasMet || ''
                                                                                }
                                                                                onChange={(e) => {
                                                                                    const copy = copyObject(currentNeedsInfo);
                                                                                    const currentNeed = copy.CurrentNeedsList.find(
                                                                                        (o) => o.OptionLabel === option.OptionLabel
                                                                                    );
                                                                                    if (currentNeed) {
                                                                                        currentNeed.HowNeedWasMet = e.target.value;
                                                                                        currentNeed.IsAI = false;
                                                                                    }
                                                                                    dispatch(setCurrentNeedsInfo(copy));
                                                                                    setIsChanged(true);
                                                                                }}
                                                                                error={
                                                                                    currentNeedsInfo.CurrentNeedsList.find(
                                                                                        (o) => o.OptionLabel === option.OptionLabel
                                                                                    )?.IsMet === YES &&
                                                                                    !currentNeedsInfo.CurrentNeedsList.find(
                                                                                        (o) => o.OptionLabel === option.OptionLabel
                                                                                    )?.HowNeedWasMet
                                                                                }
                                                                                helperText={
                                                                                    currentNeedsInfo.CurrentNeedsList.find(
                                                                                        (o) => o.OptionLabel === option.OptionLabel
                                                                                    )?.IsMet === YES &&
                                                                                    !currentNeedsInfo.CurrentNeedsList.find(
                                                                                        (o) => o.OptionLabel === option.OptionLabel
                                                                                    )?.HowNeedWasMet
                                                                                        ? 'Required'
                                                                                        : ''
                                                                                }
                                                                            />
                                                                        </Collapse>
                                                                        <Stack spacing={1}>
                                                                            <Stack direction={'row'} spacing={2} alignItems="center">
                                                                                <TextField
                                                                                    label="Comments"
                                                                                    variant="outlined"
                                                                                    size="small"
                                                                                    fullWidth
                                                                                    name="Comments"
                                                                                    placeholder="Please provide any additional comments"
                                                                                    value={
                                                                                        currentNeedsInfo.CurrentNeedsList.find(
                                                                                            (o) => o.OptionLabel === option.OptionLabel
                                                                                        )?.Comments || ''
                                                                                    }
                                                                                    onChange={(e) => {
                                                                                        const copy = copyObject(currentNeedsInfo);
                                                                                        const currentNeed = copy.CurrentNeedsList.find(
                                                                                            (o) => o.OptionLabel === option.OptionLabel
                                                                                        );
                                                                                        if (currentNeed) {
                                                                                            currentNeed.Comments = e.target.value;
                                                                                            currentNeed.IsAI = false;
                                                                                        }
                                                                                        dispatch(setCurrentNeedsInfo(copy));
                                                                                        setIsChanged(true);
                                                                                    }}
                                                                                />
                                                                                <Button
                                                                                    variant="contained"
                                                                                    disabled={
                                                                                        currentNeedsInfo.CurrentNeedsList.find(
                                                                                            (o) => o.OptionLabel === option.OptionLabel
                                                                                        )?.Comments?.length === 0
                                                                                    }
                                                                                    onClick={() => {
                                                                                        const copy = copyObject(currentNeedsInfo);
                                                                                        const currentNeed = copy.CurrentNeedsList.find(
                                                                                            (o) => o.OptionLabel === option.OptionLabel
                                                                                        );
                                                                                        if (currentNeed) {
                                                                                            handleAddNeedComment(currentNeed);
                                                                                        }
                                                                                    }}
                                                                                >
                                                                                    <AddIcon />
                                                                                </Button>
                                                                            </Stack>
                                                                            {currentNeedsInfo.CurrentNeedsList.find((o) => o.OptionLabel === option.OptionLabel)
                                                                                ?.NeedComments?.length > 0 &&
                                                                                currentNeedsInfo.CurrentNeedsList.find(
                                                                                    (o) => o.OptionLabel === option.OptionLabel
                                                                                )?.NeedComments.map((comment, index) => {
                                                                                    return (
                                                                                        <Tooltip
                                                                                            key={comment.Id || `${comment.Comment}-${index}`}
                                                                                            title={
                                                                                                <Stack>
                                                                                                    <Typography variant="body2">
                                                                                                        {(() => {
                                                                                                            const d = toDate(comment.CreatedDate);
                                                                                                            return d ? formatDate(d, 'MM/dd/yyyy hh:mm a') : '';
                                                                                                        })()}
                                                                                                    </Typography>
                                                                                                    <Typography variant="body2" fontStyle={'italic'}>
                                                                                                        {comment.CreatedBy?.Name}
                                                                                                    </Typography>
                                                                                                </Stack>
                                                                                            }
                                                                                            placement="bottom"
                                                                                        >
                                                                                            <Paper elevation={1} sx={{ padding: 1 }}>
                                                                                                <Stack direction="row" spacing={1} alignItems="flex-start" justifyContent="space-between">
                                                                                                    <Typography variant="body1" sx={{ flex: 1 }}>
                                                                                                        {comment.Comment}
                                                                                                    </Typography>
                                                                                                    <Stack direction="row" spacing={0.5}>
                                                                                                        <IconButton
                                                                                                            size="small"
                                                                                                            title="Edit comment"
                                                                                                            onClick={() =>
                                                                                                                handleOpenEditNeedComment(
                                                                                                                    option.OptionLabel,
                                                                                                                    index,
                                                                                                                    comment.Comment
                                                                                                                )
                                                                                                            }
                                                                                                        >
                                                                                                            <EditIcon fontSize="small" />
                                                                                                        </IconButton>
                                                                                                        <IconButton
                                                                                                            size="small"
                                                                                                            title="Delete comment"
                                                                                                            onClick={() =>
                                                                                                                setDeletingComment({
                                                                                                                    optionLabel: option.OptionLabel,
                                                                                                                    index,
                                                                                                                })
                                                                                                            }
                                                                                                        >
                                                                                                            <DeleteIcon fontSize="small" />
                                                                                                        </IconButton>
                                                                                                    </Stack>
                                                                                                </Stack>
                                                                                            </Paper>
                                                                                        </Tooltip>
                                                                                    );
                                                                                })}
                                                                        </Stack>
                                                                    </Stack>
                                                                </Collapse>
                                                            </Stack>
                                                        </Paper>
                                                    </Grid>
                                                );
                                            })}
                                        </Grid>
                                    </FormGroup>
                                </FormControl>

                                <Grid size={{ sm: 12, md: 12, lg: 12 }}>
                                    <hr className="hr-lite" />
                                </Grid>
                                <Grid size={6}>
                                    <FormLabel>{AssessmentQuestions.CurrentNeeds_MemberSignificantOther}</FormLabel>
                                </Grid>
                                <Grid size={6}>
                                    <TextField
                                        label="Please specify"
                                        variant="outlined"
                                        size="small"
                                        multiline
                                        rows={3}
                                        fullWidth
                                        margin="normal"
                                        name="NeedsEducationOn"
                                        value={currentNeedsInfo.NeedsEducationOn || ''}
                                        onChange={(e) => {
                                            dispatch(setCurrentNeedsInfo({ ...currentNeedsInfo, NeedsEducationOn: e.target.value }));
                                            setIsChanged(true);
                                        }}
                                    />
                                </Grid>
                                <Grid size={{ sm: 12, md: 12, lg: 12 }}>
                                    <hr className="hr-lite" />
                                </Grid>
                                <Grid size={6}>
                                    <FormLabel>{AssessmentQuestions.CurrentNeeds_MemberNeedsOther}</FormLabel>
                                </Grid>
                                <Grid size={6}>
                                    <TextField
                                        label="Please specify"
                                        variant="outlined"
                                        size="small"
                                        multiline
                                        rows={3}
                                        fullWidth
                                        margin="normal"
                                        name="NeedsOtherEnvironmentalSupport"
                                        value={currentNeedsInfo.NeedsOtherEnvironmentalSupport || ''}
                                        onChange={(e) => {
                                            dispatch(setCurrentNeedsInfo({ ...currentNeedsInfo, NeedsOtherEnvironmentalSupport: e.target.value }));
                                            setIsChanged(true);
                                        }}
                                    />
                                </Grid>
                                <Grid size={{ sm: 12, md: 12, lg: 12 }}>
                                    <hr className="hr-lite" />
                                </Grid>
                                <Grid size={6}>
                                    <FormLabel>{AssessmentQuestions.CurrentNeeds_SkillsDeficitSkills}</FormLabel>
                                </Grid>
                                <Grid size={6}>
                                    <TextField
                                        label="Please specify"
                                        variant="outlined"
                                        size="small"
                                        multiline
                                        rows={3}
                                        fullWidth
                                        margin="normal"
                                        name="SkillsAndSupportsNeeds"
                                        value={currentNeedsInfo.SkillsAndSupportsNeeds || ''}
                                        onChange={(e) => {
                                            dispatch(setCurrentNeedsInfo({ ...currentNeedsInfo, SkillsAndSupportsNeeds: e.target.value }));
                                            setIsChanged(true);
                                        }}
                                    />
                                </Grid>
                            </Grid>

                            <Grid justifyContent="flex-end" className="content-footer">
                                <Button
                                    type="submit"
                                    variant="contained"
                                    color="secondary"
                                    sx={{ marginRight: 2 }}
                                    disabled={isSaving}
                                    onClick={() => dispatch(gotoPreviousMenu())}
                                    startIcon={<ArrowBackIosIcon />}
                                >
                                    Previous
                                </Button>
                                <Button
                                    type="submit"
                                    variant="contained"
                                    disabled={isSaving}
                                    endIcon={isSaving ? <CircularProgress size={18} /> : <ArrowForwardIosIcon />}
                                    onClick={handleSubmit}
                                >
                                    Next
                                </Button>
                            </Grid>
                        </>
                    )}

                    {Boolean(currentNeedsInfoIsComplete(currentNeedsInfo) && currentNeedsInfo.CreatedBy && !isChanged) ? (
                        <AlertDialog
                            open={showSaveAlert}
                            title={`Current Needs`}
                            buttonsDirection="column-reverse"
                            showBottomCenter={true}
                            cancelText="Go Back"
                            skipText={`Current Needs Assessment Not Reviewed`}
                            okText={`Agree With Current Needs Assessment`}
                            onConfirm={() => handleOnConfirmToNext(currentNeedsInfo, true)}
                            onSkip={handleOnConfirmSkip}
                            onClose={() => setShowSaveAlert(false)}
                            isLoading={isSaving}
                            message={`Last Current Needs assessment completed on ${(() => {
                                const d = toDate(currentNeedsInfo.UpdatedDate ?? currentNeedsInfo.CreatedDate);
                                return d ? formatDate(d, 'MM/dd/yyyy') : '';
                            })()}.`}
                        />
                    ) : Boolean(currentNeedsInfoIsComplete(currentNeedsInfo)) ? (
                        <AlertDialog
                            open={showSaveAlert}
                            title="Current Needs"
                            cancelText="Go Back"
                            okText="Confirm"
                            onConfirm={() => handleOnConfirmToNext(currentNeedsInfo, true)}
                            onSkip={handleOnConfirmSkip}
                            showBottomCenter={true}
                            onClose={() => setShowSaveAlert(false)}
                            isLoading={isSaving}
                            message="I confirm and acknowledge that Current Needs is completed."
                        />
                    ) : (
                        <AlertDialog
                            open={showSaveAlert}
                            title="Current Needs"
                            showBottomCenter={true}
                            cancelText="Go Back"
                            skipText="Skip and continue"
                            okText="Confirm"
                            onConfirm={() => handleOnConfirmToNext(currentNeedsInfo, false)}
                            onSkip={handleOnConfirmSkip}
                            onClose={() => setShowSaveAlert(false)}
                            isLoading={isSaving}
                            renderBody={() =>
                                BHDiagnosticConfirmationBody({
                                    assessmentName: 'Current Needs',
                                    diagnosticType: engagementDraft.DiagnosticStatus?.DiagnosticType ?? DiagnosticType.None,
                                })
                            }
                        />
                    )}
                </Grid>
            </Grid>

            <Dialog open={Boolean(editingComment)} onClose={() => setEditingComment(null)} fullWidth maxWidth="sm">
                <DialogTitle>Edit Comment</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        margin="dense"
                        label="Comment"
                        fullWidth
                        multiline
                        rows={4}
                        value={editCommentText}
                        onChange={(e) => setEditCommentText(e.target.value)}
                    />
                </DialogContent>
                <DialogActions>
                    <Button variant="outlined" onClick={() => setEditingComment(null)}>
                        Cancel
                    </Button>
                    <Button variant="contained" onClick={handleConfirmEditNeedComment} disabled={!editCommentText.trim()}>
                        Save
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={Boolean(deletingComment)} onClose={() => setDeletingComment(null)} fullWidth maxWidth="xs">
                <DialogTitle>Delete Comment</DialogTitle>
                <DialogContent>
                    <Typography>Are you sure you want to delete this comment?</Typography>
                </DialogContent>
                <DialogActions>
                    <Button variant="outlined" onClick={() => setDeletingComment(null)}>
                        Cancel
                    </Button>
                    <Button variant="contained" color="error" onClick={handleConfirmDeleteNeedComment}>
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
        </Paper>
    );
};
export default CurrentNeeds;
